import { useEffect, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Map as MapboxBaseMap } from 'react-map-gl/mapbox';
import type {
  DeckProps,
  MapViewState,
  PickingInfo,
  Widget,
} from '@deck.gl/core';
import { DeckGL } from '@deck.gl/react';
import { CompassWidget } from '@deck.gl/widgets';
import '@deck.gl/widgets/stylesheet.css';
import {
  MAPBOX_ACCESS_TOKEN,
  MAP_STYLES,
} from '../../infrastructure/mapbox/config';
import { useCustomAreaLayer } from '../../hooks/SimViewer/MapLayer/useCustomAreaLayer';
import { useAreaLayer } from '../../hooks/SimViewer/MapLayer/useAreaLayer';
import { useAreaCityLayer } from '../../hooks/SimViewer/MapLayer/useAreaCityLayer';
import { useAreaPrefLayer } from '../../hooks/SimViewer/MapLayer/useAreaPrefLayer';
import { useReferenceAreaLayer } from '../../hooks/SimViewer/MapLayer/useReferenceAreaLayer';
import { useLinkLayer } from '../../hooks/SimViewer/MapLayer/useLinkLayer';
import { useGridHeatmapLayer } from '../../hooks/SimViewer/MapLayer/useGridHeatmapLayer';
import { useHeatmapLayer } from '../../hooks/SimViewer/MapLayer/useHeatmapLayer';
import { useArcLayer } from '../../hooks/SimViewer/MapLayer/useArcLayer';
import { useFilterPinLayer } from '../../hooks/SimViewer/MapLayer/useFilterPinLayer';
import { useSearchPinLayer } from '../../hooks/SimViewer/MapLayer/useSearchPinLayer';
import { useCustomAreaCreation } from '../../hooks/SimViewer/useCustomAreaCreation';
import { useCustomAreaEdition } from '../../hooks/SimViewer/useCustomAreaEdition';
import { useMapColor } from '../../hooks/SimViewer/useMapColor';
import { useLanguage } from '../../hooks/useLanguage';
import { useOdMapLayers } from '../../hooks/SimViewer/MapLayer/useOdMapLayers';
import {
  useMapViewState,
  useMapViewStateUpdater,
} from '../../hooks/SimViewer/useMapViewState';
import { useCurrentSimDataId } from '../../hooks/SimViewer/useCurrentSimDataId';
import { useDebounce } from '../../hooks/useDebounce';
import { useMapVisibility } from '../../hooks/SimViewer/useMapVisibility';
import { useAggregationConfig } from '../../hooks/SimViewer/useAggregationConfig';
import { SimViewerTools } from './SimViewerTools/SimViewerTools';
import { ScaleBarWidget } from './ScaleBarWidget';
import { LocatorWidget } from './LocatorWidget';

type Props = {
  handleClickMap?: DeckProps['onClick'];
};

export const Map = ({ handleClickMap }: Props): ReactNode => {
  const { t } = useTranslation();
  const { currentSimDataId } = useCurrentSimDataId();
  const { referenceAreaLayer } = useReferenceAreaLayer();
  const { areaLayer, areaLegends } = useAreaLayer();
  const { areaCityLayer, areaCityLegends } = useAreaCityLayer();
  const { areaPrefLayer, areaPrefLegends } = useAreaPrefLayer();
  const { linkLayer, linkLegends } = useLinkLayer();
  const { gridHeatmapLayer, gridHeatmapLegends } = useGridHeatmapLayer();
  const { heatmapLayer } = useHeatmapLayer();
  const { arcLayer, arcLegends } = useArcLayer();
  const { odMapContourLayer, odMapNodeLayer, odMapEdgeLayer } =
    useOdMapLayers();
  const { filterPinLayer } = useFilterPinLayer();
  const { searchPinLayer } = useSearchPinLayer();
  const { aggregationConfig } = useAggregationConfig();
  const { customAreaLayer, customAreaLabelLayer } = useCustomAreaLayer();
  const { creatingCustomArea } = useCustomAreaCreation();
  const { editingCustomArea } = useCustomAreaEdition();
  const { visibleAreaType } = useMapVisibility();
  const { mapColor } = useMapColor();
  const { mapViewState } = useMapViewState();
  const { updateMapViewState } = useMapViewStateUpdater();
  const { language } = useLanguage();
  const { debounce, cancel } = useDebounce(500);

  // データ切り替え時とページ離脱時にペンディング中の更新処理をキャンセルする
  useEffect((): (() => void) => {
    return (): void => {
      cancel();
    };
  }, [currentSimDataId, cancel]);

  const handleViewStateChange = (event: { viewState: MapViewState }): void => {
    const { viewState } = event;

    // URLに地図の状態を反映させるために状態を更新する(頻繁なURLの更新を避けるためにデバウンス処理を行う)
    // Deck.glはonViewStateChangeがなくても内部的にviewStateを管理しているため、地図の移動自体がデバウンスされることはない
    debounce((): void => {
      const { zoom, latitude, longitude, pitch, bearing } = viewState;
      updateMapViewState({ latitude, longitude, zoom, pitch, bearing });
    });
  };

  const layers = [
    referenceAreaLayer,
    areaLayer,
    areaCityLayer,
    areaPrefLayer,
    customAreaLayer,
    customAreaLabelLayer,
    linkLayer,
    gridHeatmapLayer,
    heatmapLayer,
    arcLayer,
    odMapContourLayer,
    odMapNodeLayer,
    odMapEdgeLayer,
    filterPinLayer,
    searchPinLayer,
  ];

  const widgets = useMemo((): Widget[] => {
    return [
      new LocatorWidget({
        placement: 'bottom-left',
        labels: {
          longitude: t('lon'),
          latitude: t('lat'),
          zoom: t('zoom'),
          move: t('moveToThere'),
        },
        style: {
          display: 'inline-block',
          verticalAlign: 'middle',
          margin: '0 6px 16px 16px',
        },
      }),
      new CompassWidget({
        placement: 'bottom-left',
        style: {
          display: 'inline-block',
          verticalAlign: 'middle',
          margin: '0 6px 16px 6px',
        },
      }),
      new ScaleBarWidget({
        placement: 'bottom-left',
        mapColor: mapColor,
        style: {
          display: 'inline-block',
          verticalAlign: 'middle',
          margin: '0 6px 16px 12px',
        },
      }),
    ];
  }, [mapColor, t]);

  // ダークモードで特定のレイヤーモードのときだけ、ベースマップをブレンドにすることで地名の文字を読みやすくする
  const applyMixBlend =
    mapColor === 'dark' &&
    ['grid_heatmap', 'areas', 'area_pref'].includes(
      aggregationConfig?.type || ''
    );

  return (
    <>
      <DeckGL
        initialViewState={mapViewState}
        layers={layers}
        widgets={widgets}
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          mixBlendMode: applyMixBlend ? 'screen' : 'normal',
        }}
        controller={{
          scrollZoom: true,
          doubleClickZoom: false,
          dragPan: true,
          dragRotate: true,
          touchZoom: true,
          touchRotate: false,
          keyboard: true,
        }}
        getTooltip={(pickingInfo: PickingInfo): string | null => {
          return pickingInfo.object?.tooltip || null;
        }}
        getCursor={({ isDragging }: { isDragging: boolean }): string => {
          if (['polygon', 'circle'].includes(editingCustomArea?.type || '')) {
            return isDragging ? 'grabbing' : 'grab';
          }
          if (creatingCustomArea?.type) {
            return 'crosshair';
          }
          return isDragging ? 'grabbing' : 'pointer';
        }}
        onClick={handleClickMap}
        onViewStateChange={handleViewStateChange}
      >
        <MapboxBaseMap
          key={`${mapColor}-${language}`}
          language={language}
          mapStyle={MAP_STYLES[mapColor]}
          mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
          preserveDrawingBuffer
        />
      </DeckGL>
      <SimViewerTools
        legends={{
          areas: visibleAreaType === 'city' ? areaCityLegends : areaLegends,
          area_pref: areaPrefLegends,
          grid_heatmap: gridHeatmapLegends,
          arcs: arcLegends,
          links: linkLegends,
        }}
      />
    </>
  );
};
